/**
 * The scripted canyon run: the flight `npm run perf -- --scene canyon` measures.
 *
 * ## Why this exists
 *
 * M6's acceptance criterion is a *specific flight*: "balloon canyon run at 100 ft AGL at 500 kt
 * holds 60 fps with all effects, Medium tier". None of the existing harness could produce that.
 * `perfHarness.ts` measures whatever is happening and does not fly, so before this the perf run
 * measured an aeroplane parked on a runway — which is a fine regression test for the terrain and
 * the sky and tells you nothing at all about six balloons, a firing cannon, four hundred tracers
 * and a canyon going past at 257 m/s.
 *
 * ## The trajectory is a rail, and that is deliberate
 *
 * The aircraft's position and velocity are *overwritten* each tick, after the flight model has
 * run. Two reasons, and the second is the important one:
 *
 *   - **Reproducibility.** A benchmark has to fly the same path every time or its numbers cannot
 *     be compared between runs. An autopilot at 100 ft and 500 kt in a canyon would not merely be
 *     hard to write — it would produce a *different* path on every run, and a regression would be
 *     indistinguishable from the autopilot having a slightly worse day.
 *   - **It does not fake the cost.** The flight model still runs its full tick before the state is
 *     replaced, so the physics channel measures real physics. The terrain still streams against
 *     the real position, the effects still emit against the real velocity, and the renderer still
 *     draws the real scene. The only thing scripted is *where the aeroplane goes*.
 *
 * The gun is held down for the whole run, which is the honest worst case: 100 rounds a second,
 * four hundred rounds alive at once, eighty tracer ribbons, and sparks wherever they land.
 *
 * `updateFixed(dt)`: yes — {@link CanyonRun.updateFixed}, called after the flight model's own.
 * Allocation-free after construction.
 */

import { Vector3 } from 'three';
import { DEG2RAD } from '../core/math.js';
import { knotsToMps } from '../core/units.js';

/** The run's airspeed — the criterion's 500 kt. */
export const CANYON_RUN_SPEED_KT = 500;
/** Height above the course's ground [m] — the criterion's 100 ft. */
export const CANYON_RUN_HEIGHT_M = 30.48;
/** How far before the first balloon the run starts [m]. */
const LEAD_IN_M = 2_600;
/**
 * How far past the last balloon the *scored* part of the run extends [m].
 *
 * Past this the rail keeps flying — see {@link CanyonRun.updateFixed}. It marks where the course
 * ends, not where the measurement does.
 */
const RUN_OUT_M = 4_000;

/** `?scene=canyon` — fly the balloon course instead of sitting on a runway. */
export function requestedPerfScene(
  search: string = globalThis.location?.search ?? '',
): 'default' | 'canyon' {
  return new URLSearchParams(search).get('scene')?.trim().toLowerCase() === 'canyon'
    ? 'canyon'
    : 'default';
}

export interface CanyonRunOptions {
  /** True heading down the course [deg] — must match the balloon line. */
  headingDeg: number;
  /** Ground elevation the run is flown above [m AMSL]. */
  groundElevationM: number;
  /** Course anchor in sim-frame NED, refreshed by the caller each tick. */
  anchorNed: Vector3;
}

export class CanyonRun {
  private readonly headingRad: number;
  private readonly speedMps: number;
  private readonly options: CanyonRunOptions;
  /** Distance flown along the course [m], from the lead-in point. */
  private alongM = -LEAD_IN_M;
  private finishedValue = false;

  constructor(options: CanyonRunOptions) {
    this.options = options;
    this.headingRad = options.headingDeg * DEG2RAD;
    this.speedMps = knotsToMps(CANYON_RUN_SPEED_KT);
  }

  get finished(): boolean {
    return this.finishedValue;
  }

  /** True heading the aircraft is railed to [rad]. */
  get headingRadians(): number {
    return this.headingRad;
  }

  /** How far along the run [m], negative during the lead-in. */
  get distanceM(): number {
    return this.alongM;
  }

  /**
   * Put the aeroplane where the script says, this tick.
   *
   * @param groundElevationM Ground directly beneath the aircraft [m AMSL], or `NaN` when the
   *   terrain has not been sampled yet.
   *
   * Writes into the caller's position, velocity and attitude — the flight model's own state
   * objects — so the next tick's aerodynamics, the terrain streamer and the effects all see the
   * railed trajectory rather than a shadow copy of it.
   */
  updateFixed(
    dt: number,
    positionNed: Vector3,
    velocityNed: Vector3,
    attitude: { set(x: number, y: number, z: number, w: number): unknown },
    groundElevationM: number,
  ): void {
    this.alongM += this.speedMps * dt;
    /*
     * Past the last balloon the rail keeps going, and that is the point.
     *
     * The first version stopped railing here and handed the aeroplane back to the flight model —
     * which, at 100 ft and 500 kt with nobody flying it, meant the back half of every measurement
     * window was an uncontrolled aircraft descending into a canyon rather than the flight
     * condition the acceptance criterion names. A 40 s window measured about 16 s of the right
     * thing.
     *
     * So `finished` now means "past the course", which is what the score and the trigger want to
     * know, and the trajectory simply continues: same heading, same height above the anchor, same
     * 500 kt. Continuing straight rather than looping back to the start is deliberate too — a
     * wrap would teleport the aeroplane 6.6 km backwards, and the terrain churn from that would be
     * an artefact of the benchmark rather than a measurement of anything.
     */
    if (this.alongM > RUN_OUT_M) this.finishedValue = true;

    const cos = Math.cos(this.headingRad);
    const sin = Math.sin(this.headingRad);
    const anchor = this.options.anchorNed;

    /*
     * A hundred feet above the ground that is actually there.
     *
     * The first version held the aircraft at a fixed height above the *course anchor's* elevation,
     * which is only "AGL" while it is over the course. Flying on past the last balloon it kept
     * that altitude over terrain of a completely different height and went underground — and a
     * camera below the terrain makes the quadtree's screen-space error selection ask for
     * everything at once. A 55 s measurement never returned; a 17 s one, finishing before the
     * aircraft left the canyon, completed fine. That is what localised it.
     *
     * Taking the height from the sampled ground under the aeroplane is both what the acceptance
     * criterion actually says and what makes the rail safe to fly indefinitely. The anchor is
     * still what places it horizontally.
     */
    const groundM = Number.isNaN(groundElevationM)
      ? -anchor.z
      : groundElevationM;
    positionNed.set(
      anchor.x + cos * this.alongM,
      anchor.y + sin * this.alongM,
      // NED is down-positive, so height above ground is a negative z.
      -(groundM + CANYON_RUN_HEIGHT_M),
    );
    velocityNed.set(cos * this.speedMps, sin * this.speedMps, 0);
    // Wings level on the course heading: a rotation about the NED down axis and nothing else.
    attitude.set(0, 0, Math.sin(this.headingRad / 2), Math.cos(this.headingRad / 2));
  }
}
